import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  CreditCard, CheckCircle, Loader2, ArrowRight, ArrowLeft, Check,
  Package, AlertCircle, Edit
} from "lucide-react";
import { CardData, validateCardNumber, formatCardNumber, getCardBrand } from "@/lib/pagarme";
import { formatCPF, formatPhone, formatCEP, formatPrice } from "@/lib/formatters";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";

interface ExtensionPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extension: {
    id: string;
    title: string;
    short_description: string;
    icon_name: string;
    price_cents: number;
    pricing_type: string;
  };
  onSuccess: () => void;
}

type WizardStep = 'profile-review' | 'card-form' | 'processing' | 'success';

interface ProfileData {
  full_name?: string | null;
  cpf?: string | null;
  phone?: string | null;
  address_zipcode?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_neighborhood?: string | null;
  address_city?: string | null;
  address_state?: string | null;
}

// Componente de indicador de progresso
function StepIndicator({ currentStep }: { currentStep: WizardStep }) {
  const steps = ['profile-review', 'card-form', 'success'];
  const currentStepIndex = steps.indexOf(currentStep === 'processing' ? 'card-form' : currentStep);

  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = index === currentStepIndex;
        const isCompleted = index < currentStepIndex;

        return (
          <div key={step} className="flex items-center gap-2">
            <motion.div
              initial={false}
              animate={{
                scale: isActive ? 1.1 : 1,
              }}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all",
                isActive && "bg-primary text-primary-foreground ring-4 ring-primary/20",
                isCompleted && "bg-green-600 text-white",
                !isActive && !isCompleted && "bg-muted text-muted-foreground"
              )}
            >
              {isCompleted ? <Check className="h-4 w-4" /> : stepNumber}
            </motion.div>
            {stepNumber < 3 && (
              <div
                className={cn(
                  "w-12 h-1 rounded-full transition-colors",
                  isCompleted ? "bg-green-600" : "bg-muted"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ExtensionPaymentModal({
  open,
  onOpenChange,
  extension,
  onSuccess
}: ExtensionPaymentModalProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('profile-review');
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [progressMessage, setProgressMessage] = useState<string>("");
  const [cardData, setCardData] = useState<CardData>({
    number: "",
    holder_name: "",
    exp_month: "",
    exp_year: "",
    cvv: "",
  });
  const [existingCards, setExistingCards] = useState<any[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(true);

  const cardNumberInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadProfile();
      loadExistingCards();
    } else {
      // Reset quando fechar
      setCurrentStep('profile-review');
      setCardData({
        number: "",
        holder_name: "",
        exp_month: "",
        exp_year: "",
        cvv: "",
      });
      setSelectedCardId(null);
      setUseNewCard(true);
    }
  }, [open]);

  // Auto-preencher nome no cartão quando entrar no passo 2
  useEffect(() => {
    if (currentStep === 'card-form' && profileData?.full_name && !cardData.holder_name) {
      setCardData(prev => ({
        ...prev,
        holder_name: profileData.full_name.toUpperCase()
      }));
    }
  }, [currentStep, profileData]);

  // Focar automaticamente no campo número do cartão
  useEffect(() => {
    if (currentStep === 'card-form' && useNewCard) {
      setTimeout(() => {
        cardNumberInputRef.current?.focus();
      }, 100);
    }
  }, [currentStep, useNewCard]);

  async function loadProfile() {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Sessão não encontrada");
        onOpenChange(false);
        return;
      }

      setUserEmail(user.email || "");

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setProfileData(profile);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast.error("Erro ao carregar perfil");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }

  async function loadExistingCards() {
    try {
      const { data, error } = await supabase.functions.invoke('pagarme-list-cards');

      if (error) throw error;

      if (data?.cards && data.cards.length > 0) {
        setExistingCards(data.cards);
        setUseNewCard(false);
        setSelectedCardId(data.cards.find((c: any) => c.is_default)?.id || data.cards[0].id);
      }
    } catch (error) {
      console.error('Error loading cards:', error);
    }
  }

  async function handleNextToPayment() {
    setCurrentStep('card-form');
  }

  async function handleProcessPayment() {
    if (useNewCard) {
      // Validar dados do cartão
      if (!cardData.number || !cardData.holder_name || !cardData.exp_month || !cardData.exp_year || !cardData.cvv) {
        toast.error("Preencha todos os dados do cartão");
        return;
      }

      const cleanedNumber = cardData.number.replace(/\s/g, '');
      if (!validateCardNumber(cleanedNumber)) {
        toast.error("Número de cartão inválido");
        return;
      }
    } else {
      // Validar seleção de cartão existente
      if (!selectedCardId) {
        toast.error("Selecione um cartão");
        return;
      }
    }

    setLoading(true);
    setCurrentStep('processing');
    setProgressMessage('💳 Processando pagamento...');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão não encontrada');

      let cardId = selectedCardId;

      // Se for novo cartão, criar no Pagar.me primeiro
      if (useNewCard) {
        setProgressMessage('🔐 Criando cartão seguro...');

        const { data: cardResult, error: cardError } = await supabase.functions.invoke('pagarme-create-card-direct', {
          body: {
            card: {
              number: cardData.number.replace(/\s/g, ''),
              holder_name: cardData.holder_name,
              exp_month: parseInt(cardData.exp_month),
              exp_year: parseInt(cardData.exp_year),
              cvv: cardData.cvv,
            }
          }
        });

        if (cardError) throw cardError;
        if (!cardResult?.card_id) throw new Error('Falha ao criar cartão');

        cardId = cardResult.card_id;
      }

      setProgressMessage('📦 Contratando aplicativo...');

      // Processar pagamento do aplicativo
      // TODO: Criar edge function específica para processar pagamento de extensão
      // Por ora, apenas inserir na tabela user_extensions
      const { error: extensionError } = await supabase
        .from('user_extensions')
        .insert({
          user_id: user.id,
          extension_id: extension.id,
          status: 'active',
        });

      if (extensionError) throw extensionError;

      setProgressMessage('✅ Pagamento aprovado!');

      setTimeout(() => {
        setCurrentStep('success');
        setLoading(false);
      }, 1000);

    } catch (error: any) {
      console.error('Error processing payment:', error);
      toast.error(error.message || "Erro ao processar pagamento");
      setLoading(false);
      setCurrentStep('card-form');
      setProgressMessage('');
    }
  }

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || Package;
    return Icon;
  };

  const Icon = getIcon(extension.icon_name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contratar Aplicativo</DialogTitle>
        </DialogHeader>

        <StepIndicator currentStep={currentStep} />

        <AnimatePresence mode="wait">
          {/* Passo 1: Confirmação dos Dados */}
          {currentStep === 'profile-review' && (
            <motion.div
              key="profile-review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Card do aplicativo */}
              <Card className="bg-gradient-to-br from-primary/10 via-purple-500/5 to-primary/10 border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Aplicativo Selecionado</div>
                      <div className="text-xl font-bold">{extension.title}</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{extension.short_description}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-primary">{formatPrice(extension.price_cents)}</span>
                    <span className="text-muted-foreground">/{extension.pricing_type === "one_time" ? "único" : "mês"}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Dados do assinante */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Seus Dados
                    <Button variant="outline" size="sm" onClick={() => window.location.href = '/app/conta'}>
                      <Edit className="h-3 w-3 mr-1" />
                      Editar
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Nome</p>
                      <p className="font-medium">{profileData?.full_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">CPF</p>
                      <p className="font-medium">{profileData?.cpf ? formatCPF(profileData.cpf) : "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">E-mail</p>
                      <p className="font-medium">{userEmail}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Telefone</p>
                      <p className="font-medium">{profileData?.phone ? formatPhone(profileData.phone) : "—"}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground mb-1">Endereço</p>
                      <p className="font-medium">
                        {profileData?.address_street ? (
                          `${profileData.address_street}, ${profileData.address_number} - ${profileData.address_neighborhood}, ${profileData.address_city}/${profileData.address_state}`
                        ) : "—"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={handleNextToPayment} className="w-full" size="lg">
                Continuar para Pagamento
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* Passo 2: Dados de Pagamento */}
          {currentStep === 'card-form' && (
            <motion.div
              key="card-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Resumo do app */}
              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-8 w-8 text-primary" />
                      <div>
                        <p className="font-medium">{extension.title}</p>
                        <p className="text-sm text-muted-foreground">{extension.short_description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{formatPrice(extension.price_cents)}</p>
                      <p className="text-xs text-muted-foreground">/{extension.pricing_type === "one_time" ? "único" : "mês"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Seleção de cartão */}
              {existingCards.length > 0 && (
                <div className="space-y-3">
                  <Label>Forma de Pagamento</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={!useNewCard ? "default" : "outline"}
                      onClick={() => setUseNewCard(false)}
                      className="flex-1"
                    >
                      Usar Cartão Cadastrado
                    </Button>
                    <Button
                      type="button"
                      variant={useNewCard ? "default" : "outline"}
                      onClick={() => setUseNewCard(true)}
                      className="flex-1"
                    >
                      Novo Cartão
                    </Button>
                  </div>
                </div>
              )}

              {/* Cartões existentes */}
              {!useNewCard && existingCards.length > 0 && (
                <div className="space-y-3">
                  <Label>Selecione o Cartão</Label>
                  <div className="space-y-2">
                    {existingCards.map((card) => (
                      <Card
                        key={card.id}
                        className={cn(
                          "cursor-pointer transition-all hover:shadow-md",
                          selectedCardId === card.id && "ring-2 ring-primary"
                        )}
                        onClick={() => setSelectedCardId(card.id)}
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CreditCard className="h-5 w-5" />
                              <div>
                                <p className="font-medium">{card.brand} •••• {card.last_four_digits}</p>
                                <p className="text-sm text-muted-foreground">
                                  Válido até {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                                </p>
                              </div>
                            </div>
                            {card.is_default && (
                              <Badge variant="secondary">Padrão</Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário de novo cartão */}
              {useNewCard && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="card_number">Número do Cartão</Label>
                    <Input
                      ref={cardNumberInputRef}
                      id="card_number"
                      placeholder="0000 0000 0000 0000"
                      value={cardData.number}
                      onChange={(e) => {
                        const formatted = formatCardNumber(e.target.value);
                        setCardData({ ...cardData, number: formatted });
                      }}
                      maxLength={19}
                    />
                    {cardData.number && (
                      <p className="text-xs text-muted-foreground">
                        Bandeira: {getCardBrand(cardData.number)}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="holder_name">Nome no Cartão</Label>
                    <Input
                      id="holder_name"
                      placeholder="Nome como está no cartão"
                      value={cardData.holder_name}
                      onChange={(e) => setCardData({ ...cardData, holder_name: e.target.value.toUpperCase() })}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="exp_month">Mês</Label>
                      <Input
                        id="exp_month"
                        placeholder="MM"
                        value={cardData.exp_month}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setCardData({ ...cardData, exp_month: value });
                        }}
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="exp_year">Ano</Label>
                      <Input
                        id="exp_year"
                        placeholder="AAAA"
                        value={cardData.exp_year}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCardData({ ...cardData, exp_year: value });
                        }}
                        maxLength={4}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cvv">CVV</Label>
                      <Input
                        id="cvv"
                        placeholder="123"
                        type="password"
                        value={cardData.cvv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCardData({ ...cardData, cvv: value });
                        }}
                        maxLength={4}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCurrentStep('profile-review')} className="flex-1">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <Button onClick={handleProcessPayment} className="flex-1">
                  Confirmar Pagamento
                  <CreditCard className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Passo 3: Processando */}
          {currentStep === 'processing' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-12"
            >
              <Loader2 className="h-16 w-16 text-primary animate-spin mb-4" />
              <p className="text-lg font-medium">{progressMessage}</p>
              <p className="text-sm text-muted-foreground mt-2">Aguarde alguns instantes...</p>
            </motion.div>
          )}

          {/* Passo 4: Sucesso */}
          {currentStep === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-12 space-y-6"
            >
              <div className="relative">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center"
                >
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </motion.div>
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold">Aplicativo Contratado!</h3>
                <p className="text-muted-foreground">Seu pagamento foi processado com sucesso</p>
              </div>

              <Card className="w-full">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Aplicativo</span>
                    <span className="font-medium">{extension.title}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Valor</span>
                    <span className="font-medium">{formatPrice(extension.price_cents)}</span>
                  </div>
                  {extension.pricing_type !== "one_time" && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Próxima cobrança</span>
                      <span className="font-medium">
                        {new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Button onClick={onSuccess} className="w-full" size="lg">
                Começar a Usar
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
